import { useEffect } from "react";
import { useLang } from "../lib/LanguageContext";

const DEFAULTS = {
    siteName: "Kompleksi Dea Radhimë",
    image:
        "https://static.prod-images.emergentagent.com/jobs/a7e20be5-2468-45b8-9b46-fdcb25958081/images/3f0c9338318ac813c692ca42dff57001c9b52c9f19b81efbf1020e92659e824e.png",
};

const SUPPORTED_LANGS = ["al", "en", "it", "de"];
const HREFLANG_MAP = { al: "sq-AL", en: "en", it: "it-IT", de: "de-DE" };
const OG_LOCALE_MAP = { al: "sq_AL", en: "en_US", it: "it_IT", de: "de_DE" };

/** Set or update a <meta> tag in document.head. */
function setMeta({ name, property, content }) {
    if (!content) return null;
    const attr = property ? "property" : "name";
    const key = property || name;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
    return el;
}

function setLink(rel, href, extra = {}) {
    const selector = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}`;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
    return el;
}

/** Replace all hreflang alternate links with current ones. */
function setHreflangAlternates(path) {
    if (typeof window === "undefined") return;
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    const origin = window.location.origin;
    SUPPORTED_LANGS.forEach((l) => {
        const el = document.createElement("link");
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", HREFLANG_MAP[l]);
        el.setAttribute("href", `${origin}${path}?lang=${l}`);
        document.head.appendChild(el);
    });
    // x-default points to English version
    const xd = document.createElement("link");
    xd.setAttribute("rel", "alternate");
    xd.setAttribute("hreflang", "x-default");
    xd.setAttribute("href", `${origin}${path}`);
    document.head.appendChild(xd);
}

/** Maintains an array of JSON-LD scripts so multiple schemas can coexist. */
function setJsonLD(schemas) {
    // Remove all previous SEO JSON-LD scripts
    document.head.querySelectorAll('script[type="application/ld+json"][data-seo="dea"]').forEach((el) => el.remove());
    const list = Array.isArray(schemas) ? schemas.filter(Boolean) : schemas ? [schemas] : [];
    list.forEach((schema) => {
        const el = document.createElement("script");
        el.setAttribute("type", "application/ld+json");
        el.setAttribute("data-seo", "dea");
        el.textContent = JSON.stringify(schema);
        document.head.appendChild(el);
    });
}

export default function SEO({ title, description, image, path = "/", schema, schemas }) {
    const { lang } = useLang();

    useEffect(() => {
        const url = typeof window !== "undefined" ? window.location.origin + path : "";
        const fullTitle = title
            ? `${title} — ${DEFAULTS.siteName}`
            : `${DEFAULTS.siteName} — Luxury Seaside Hotel · Albanian Riviera`;
        const desc =
            description ||
            (lang === "al"
                ? "Kompleksi Dea Radhimë — hotel luksoz buzë detit në Rivierën Shqiptare, Vlorë. Dhoma, restorant, plazh privat, bar."
                : "Kompleksi Dea Radhimë — luxury seaside hotel on the Albanian Riviera, Vlorë. Rooms, restaurant, private beach, bar.");
        const img = image || DEFAULTS.image;

        document.title = fullTitle;
        document.documentElement.lang = lang;

        setMeta({ name: "description", content: desc });
        setMeta({
            name: "keywords",
            content:
                "Kompleksi Dea, Radhimë, Vlorë hotel, Albanian Riviera, luxury hotel Albania, hotel buzë detit, plazh privat",
        });
        setMeta({ name: "robots", content: "index, follow, max-image-preview:large" });
        setMeta({ name: "author", content: DEFAULTS.siteName });
        setLink("canonical", url);

        // Hreflang alternates for multi-language SEO
        setHreflangAlternates(path);

        // Open Graph
        setMeta({ property: "og:type", content: "website" });
        setMeta({ property: "og:title", content: fullTitle });
        setMeta({ property: "og:description", content: desc });
        setMeta({ property: "og:image", content: img });
        setMeta({ property: "og:url", content: url });
        setMeta({ property: "og:site_name", content: DEFAULTS.siteName });
        setMeta({ property: "og:locale", content: OG_LOCALE_MAP[lang] || "en_US" });

        // Twitter
        setMeta({ name: "twitter:card", content: "summary_large_image" });
        setMeta({ name: "twitter:title", content: fullTitle });
        setMeta({ name: "twitter:description", content: desc });
        setMeta({ name: "twitter:image", content: img });

        // Merge legacy `schema` prop with new `schemas` array
        const all = [];
        if (schema) all.push(schema);
        if (Array.isArray(schemas)) all.push(...schemas.filter(Boolean));
        setJsonLD(all.length ? all : null);
    }, [title, description, image, path, schema, schemas, lang]);

    return null;
}

/* ============================
 * Schema.org helper builders
 * ============================ */

function siteUrl() {
    return typeof window !== "undefined" ? window.location.origin : "";
}

export function hotelSchema({ site, rooms, reviews }) {
    const ratings = (reviews || []).map((r) => Number(r.rating) || 5);
    const avgRating = ratings.length
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
        : null;
    return {
        "@context": "https://schema.org",
        "@type": ["Hotel", "LodgingBusiness", "LocalBusiness"],
        "@id": `${siteUrl()}/#hotel`,
        name: "Kompleksi Dea Radhimë",
        description: site?.about_en || "Luxury seaside hotel on the Albanian Riviera",
        url: siteUrl(),
        image: DEFAULTS.image,
        telephone: site?.phone || "0682116731",
        email: site?.email || "info@dearadhime.al",
        priceRange: "€€",
        currenciesAccepted: "EUR, ALL",
        paymentAccepted: "Cash, Credit Card, Bank Transfer",
        checkinTime: "14:00",
        checkoutTime: "11:00",
        address: {
            "@type": "PostalAddress",
            streetAddress: "Radhimë",
            addressLocality: "Radhimë",
            addressRegion: "Vlorë",
            postalCode: "9425",
            addressCountry: "AL",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: 40.3800,
            longitude: 19.4500,
        },
        openingHoursSpecification: [
            {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                opens: "00:00",
                closes: "23:59",
            },
        ],
        amenityFeature: [
            { "@type": "LocationFeatureSpecification", name: "Free Wi-Fi", value: true },
            { "@type": "LocationFeatureSpecification", name: "Restaurant", value: true },
            { "@type": "LocationFeatureSpecification", name: "Bar", value: true },
            { "@type": "LocationFeatureSpecification", name: "Private Beach", value: true },
            { "@type": "LocationFeatureSpecification", name: "Free Parking", value: true },
            { "@type": "LocationFeatureSpecification", name: "Air Conditioning", value: true },
            { "@type": "LocationFeatureSpecification", name: "Sea View", value: true },
        ],
        starRating: { "@type": "Rating", ratingValue: "4" },
        ...(avgRating && ratings.length
            ? {
                  aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: avgRating,
                      reviewCount: ratings.length,
                      bestRating: "5",
                      worstRating: "1",
                  },
              }
            : {}),
        makesOffer: (rooms || []).map((r) => ({
            "@type": "Offer",
            name: r.title_en || r.title_al,
            price: r.price,
            priceCurrency: r.currency || "EUR",
            availability: "https://schema.org/InStock",
        })),
        sameAs: [
            site?.instagram ? `https://instagram.com/${site.instagram.replace(/^@/, "")}` : null,
        ].filter(Boolean),
    };
}

export function roomSchema(room) {
    if (!room) return null;
    return {
        "@context": "https://schema.org",
        "@type": "HotelRoom",
        name: room.title_en || room.title_al,
        description: room.description_en || room.description_al,
        image: room.images?.[0] ? `${siteUrl()}/api/files/${room.images[0]}` : DEFAULTS.image,
        occupancy: {
            "@type": "QuantitativeValue",
            maxValue: room.capacity || 2,
        },
        offers: {
            "@type": "Offer",
            price: room.price,
            priceCurrency: room.currency || "EUR",
            availability: "https://schema.org/InStock",
            url: `${siteUrl()}/book?room=${room.slug}`,
        },
        amenityFeature: (room.features_en || []).map((f) => ({
            "@type": "LocationFeatureSpecification",
            name: f,
            value: true,
        })),
    };
}

export function reviewsSchema(reviews) {
    if (!reviews?.length) return null;
    return {
        "@context": "https://schema.org",
        "@type": "Hotel",
        "@id": `${siteUrl()}/#hotel`,
        name: "Kompleksi Dea Radhimë",
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: (
                reviews.reduce((s, r) => s + (Number(r.rating) || 5), 0) / reviews.length
            ).toFixed(1),
            reviewCount: reviews.length,
            bestRating: "5",
            worstRating: "1",
        },
        review: reviews.slice(0, 20).map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name || "Guest" },
            reviewRating: {
                "@type": "Rating",
                ratingValue: String(r.rating || 5),
                bestRating: "5",
            },
            reviewBody: r.text_en || r.text_al || "",
            datePublished: r.created_at || undefined,
        })),
    };
}

export function faqSchema(items) {
    if (!items?.length) return null;
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: items.map((it) => ({
            "@type": "Question",
            name: it.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: it.a,
            },
        })),
    };
}

export function breadcrumbSchema(crumbs) {
    if (!crumbs?.length) return null;
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: c.url?.startsWith("http") ? c.url : `${siteUrl()}${c.url || "/"}`,
        })),
    };
}

export function articleSchema(post, lang = "en") {
    if (!post) return null;
    const title = post[`title_${lang}`] || post.title_en || post.title_al;
    const desc = post[`excerpt_${lang}`] || post.excerpt_en || post.excerpt_al;
    return {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description: desc,
        image: post.cover_image ? `${siteUrl()}/api/files/${post.cover_image}` : DEFAULTS.image,
        author: { "@type": "Organization", name: DEFAULTS.siteName },
        publisher: {
            "@type": "Organization",
            name: DEFAULTS.siteName,
            logo: { "@type": "ImageObject", url: DEFAULTS.image },
        },
        datePublished: post.published_at || post.created_at,
        dateModified: post.updated_at || post.published_at || post.created_at,
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `${siteUrl()}/blog/${post.slug}`,
        },
    };
}
